import { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Typography,
  Skeleton,
  Box,
  Card,
  CircularProgress,
  Checkbox,
  Divider,
  Tooltip,
  TextField,
  MenuItem
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, gql } from '@apollo/client';
import Page from '../components/Page';
import { useAccount } from '../hooks/useAccount';
import ContainerMessage from '../components/ContainerMessage';
import HelperTooltip from '../components/HelperTooltip';

const CREATE_BLACKLIST = gql`
  mutation CreateBlacklist($input: CreateBlacklistInput!) {
    createBlacklist(input: $input) {
      domain
      typeItemId
      type
      itemId
      createdBy
      validTo
      createdAt
      updatedAt
    }
  }
`;

const DELETE_BLACKLIST = gql`
  mutation DeleteBlacklist($input: DeleteBlacklistInput!) {
    deleteBlacklist(input: $input) {
      domain
      typeItemId
      type
      itemId
      createdBy
      validTo
      createdAt
      updatedAt
    }
  }
`;

const LIST_CATEGORIES = gql`
  query listCategories($domain: ID!, $limit: Int!, $nextToken: String) {
    listCategorys(domain: $domain, limit: $limit, nextToken: $nextToken, sortDirection: ASC) {
      items {
        categoryId
        title
        titles
        imgLink
        link
        name
      }
      nextToken
    }
  }
`;

const GET_ESHOP_LANGUAGES = gql`
  query getEshopLanguages($eshopId: ID!) {
    getEshop(id: $eshopId) {
      platform
      languageIds
    }
  }
`;

const LIST_BLACKLISTS = gql`
  query listBlacklists($domain: ID!, $limit: Int!, $nextToken: String) {
    listBlacklists(domain: $domain, limit: $limit, nextToken: $nextToken) {
      items {
        itemId
        domain
        typeItemId
        type
      }
      nextToken
    }
  }
`;

const limit = 50;

// Only UpGates stores a title per language (Category.titles); one row per category holds all of
// them. A single language needs no selector.
const categoryLanguages = (eshop) => {
  const langs = eshop?.languageIds?.filter(Boolean) || [];
  return eshop?.platform === 'upgates' && langs.length > 1 ? langs : [];
};

// titles arrives as a JSON string (AWSJSON); rows written before titles existed have none.
const parseTitles = (titles) => {
  if (!titles) return null;
  try {
    const parsed = typeof titles === 'string' ? JSON.parse(titles) : titles;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (e) {
    return null;
  }
};

export default function EcommerceShop() {
  const { t } = useTranslation();
  const {
    eshop: { domain, id }
  } = useAccount();
  // null = not chosen yet (defaults to the eshop's first language)
  const [selectedLang, setSelectedLang] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [allBlacklistedItems, setAllBlacklistedItems] = useState([]);
  const [isLoadingAll, setIsLoadingAll] = useState(false);
  const [isLoadingBlacklist, setIsLoadingBlacklist] = useState(false);
  const [toggleStates, setToggleStates] = useState({});
  const [loadingCheckboxes, setLoadingCheckboxes] = useState({});

  const { data: eshopData } = useQuery(GET_ESHOP_LANGUAGES, {
    variables: { eshopId: id },
    skip: !id
  });
  const languages = useMemo(() => categoryLanguages(eshopData?.getEshop), [eshopData]);
  const lang = selectedLang ?? languages[0] ?? '';

  // Another eshop has other languages: drop a choice made for the previous one.
  useEffect(() => {
    setSelectedLang(null);
  }, [domain]);

  const { loading, error, refetch } = useQuery(LIST_CATEGORIES, {
    variables: {
      domain,
      limit,
      nextToken: null
    },
    fetchPolicy: 'network-only'
  });

  const { loading: blacklistLoading, refetch: refetchBlacklist } = useQuery(LIST_BLACKLISTS, {
    variables: {
      domain,
      limit,
      nextToken: null
    },
    fetchPolicy: 'network-only'
  });

  // Load all blacklisted items when component mounts or domain changes
  useEffect(() => {
    const loadAllBlacklistedItems = async () => {
      if (!domain) return;

      setIsLoadingBlacklist(true);
      setAllBlacklistedItems([]);

      let allItems = [];

      const fetchBlacklistedItems = async (nextToken = null) => {
        try {
          const result = await refetchBlacklist({
            domain,
            limit,
            nextToken
          });

          const items = result.data?.listBlacklists?.items || [];
          allItems = [...allItems, ...items];

          const newNextToken = result.data?.listBlacklists?.nextToken;

          if (newNextToken) {
            return fetchBlacklistedItems(newNextToken);
          }
          return null;
        } catch (error) {
          console.error('Error loading blacklisted items:', error);
          throw error;
        }
      };

      try {
        await fetchBlacklistedItems();
        setAllBlacklistedItems(allItems);
      } catch (error) {
        console.error('Error loading all blacklisted items:', error);
      } finally {
        setIsLoadingBlacklist(false);
      }
    };

    loadAllBlacklistedItems();
  }, [domain, refetchBlacklist]);

  // Load all categories when component mounts or domain changes
  useEffect(() => {
    const loadAllCategories = async () => {
      if (!domain) return;

      setIsLoadingAll(true);
      setAllCategories([]);

      let allItems = [];

      const fetchCategories = async (nextToken = null) => {
        try {
          const result = await refetch({
            domain,
            limit,
            nextToken
          });

          const items = result.data?.listCategorys?.items || [];
          allItems = [...allItems, ...items];

          const newNextToken = result.data?.listCategorys?.nextToken;

          if (newNextToken) {
            return fetchCategories(newNextToken);
          }
          return null;
        } catch (error) {
          console.error('Error loading categories:', error);
          throw error;
        }
      };

      try {
        await fetchCategories();
        setAllCategories(allItems);
      } catch (error) {
        console.error('Error loading all categories:', error);
      } finally {
        setIsLoadingAll(false);
      }
    };

    loadAllCategories();
  }, [domain, refetch]);

  // Initialize all toggles as activated when data loads
  useEffect(() => {
    if (allCategories.length > 0 && allBlacklistedItems.length >= 0) {
      const initialStates = {};
      const blacklistedItems = allBlacklistedItems;

      allCategories.forEach((item) => {
        // Check if this category is in the blacklist
        const isBlacklisted = blacklistedItems.some(
          (blacklistItem) =>
            blacklistItem.typeItemId === `cate#${item.categoryId}` && blacklistItem.type === 'cate'
        );

        // If blacklisted, checkbox should be unchecked (false), otherwise checked (true)
        initialStates[item.categoryId] = !isBlacklisted;
      });

      setToggleStates(initialStates);
    }
  }, [allCategories, allBlacklistedItems]);

  // GraphQL mutations
  const [addToBlacklist] = useMutation(CREATE_BLACKLIST, {
    onCompleted: (data) => {
      // Optimistically update local state after successful creation
      const newBlacklistItem = data.createBlacklist;
      setAllBlacklistedItems((prev) => [...prev, newBlacklistItem]);
    },
    onError: (error) => {
      console.error('Error adding to blacklist:', error);
    }
  });

  const [removeFromBlacklist] = useMutation(DELETE_BLACKLIST, {
    onCompleted: (data) => {
      // Optimistically update local state after successful deletion
      const deletedItem = data.deleteBlacklist;
      setAllBlacklistedItems((prev) =>
        prev.filter((item) => item.typeItemId !== deletedItem.typeItemId)
      );
    },
    onError: (error) => {
      console.error('Error removing from blacklist:', error);
    }
  });

  const handleToggleChange = async (categoryId) => {
    const category = allCategories.find((cat) => cat.categoryId === categoryId);
    if (!category) return;

    // Set loading state for this specific checkbox
    setLoadingCheckboxes((prev) => ({
      ...prev,
      [categoryId]: true
    }));

    try {
      const currentState = toggleStates[categoryId];

      if (currentState) {
        // Currently checked (not blacklisted), so we need to create blacklist (disable)
        await addToBlacklist({
          variables: {
            input: {
              domain,
              itemId: categoryId,
              type: 'cate',
              typeItemId: `cate#${categoryId}`
            }
          }
        });
      } else {
        // Currently unchecked (blacklisted), so we need to delete blacklist (enable)
        await removeFromBlacklist({
          variables: {
            input: {
              domain,
              typeItemId: `cate#${categoryId}`
            }
          }
        });
      }

      // Update local state
      setToggleStates((prev) => ({
        ...prev,
        [categoryId]: !prev[categoryId]
      }));
    } catch (error) {
      console.error('Error updating blacklist:', error);
      // Optionally show an error message to the user here
    } finally {
      // Clear loading state
      setLoadingCheckboxes((prev) => ({
        ...prev,
        [categoryId]: false
      }));
    }
  };

  if (error) return <h1>ERROR LOADING DATA</h1>;

  // The toggles are keyed by categoryId, so switching language only changes what is listed and
  // how it is titled, never a blacklist row.
  const categories = allCategories
    .map((item) => {
      const titles = parseTitles(item.titles);
      return { item, titles };
    })
    // A row with titles that has none in this language is not in that language's storefront.
    // Rows without titles (not yet re-synced) stay listed under their title.
    .filter(({ titles }) => !lang || !titles || Boolean(titles[lang]))
    .map(({ item, titles }) => ({
      id: item.categoryId,
      categoryId: item.categoryId,
      title: (lang && titles?.[lang]) || item.title,
      imgLink: item.imgLink,
      link: item.link,
      name: (lang && titles?.[lang]) || item.name
    }));

  return (
    <Page title={t('pages.categories')}>
      <Container>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 5
          }}
        >
          <Typography variant="h4">
            {t('general.categories')}
            <Box
              component="span"
              sx={{
                marginLeft: 1
              }}
            >
              {categories.length > 0 && `(${categories.length})`}
            </Box>
          </Typography>
          {languages.length > 0 && (
            <TextField
              select
              size="small"
              label={t('products.language')}
              value={lang}
              onChange={(e) => setSelectedLang(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              {languages.map((l) => (
                <MenuItem key={l} value={l}>
                  {l}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Typography variant="caption">
            {isLoadingAll || loading ? (
              <Skeleton width={100} />
            ) : (
              t('categories.loaded_categories_count', {
                count: categories.length
              })
            )}
            <HelperTooltip title={t('categories.last_update_tooltip')} />
          </Typography>
        </Box>

        {(isLoadingAll || isLoadingBlacklist || loading || blacklistLoading) && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4
            }}
          >
            <CircularProgress size={60} />
            <Typography variant="body1" sx={{ mt: 2 }}>
              {(() => {
                if (isLoadingAll) return t('general.loading_all_categories');
                if (isLoadingBlacklist) return t('general.loading_blacklist');
                return t('general.loading');
              })()}
            </Typography>
          </Box>
        )}

        {!isLoadingAll &&
        !isLoadingBlacklist &&
        !loading &&
        !blacklistLoading &&
        categories.length === 0 ? (
          <ContainerMessage
            title={t('categories.no_categories_title')}
            subtitle={t('categories.no_categories_subtitle')}
          />
        ) : (
          !isLoadingAll &&
          !isLoadingBlacklist &&
          !loading &&
          !blacklistLoading && (
            <Card sx={{ p: 3 }}>
              {/* Header */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr auto', sm: '1fr 1fr' },
                  alignItems: 'center',
                  py: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  mb: 1,
                  gap: 2
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {t('general.category')}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center'
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', paddingRight: '6px' }}>
                    {t('categories.recommend')}
                  </Typography>
                  <HelperTooltip title={t('categories.recommendable_info')} placement="top" />
                </Box>
              </Box>
              {categories.map((category, index) => {
                // Check if this category is in the blacklist
                const isBlacklisted = allBlacklistedItems.some(
                  (blacklistItem) =>
                    blacklistItem.typeItemId === `cate#${category.id}` &&
                    blacklistItem.type === 'cate'
                );

                // If blacklisted, checkbox should be unchecked (false), otherwise checked (true)
                const isEnabled = !isBlacklisted;
                const isLoading = loadingCheckboxes[category.id] || false;

                return (
                  <Box key={category.id}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr auto', sm: '1fr 1fr' },
                        alignItems: 'center',
                        py: 2,
                        gap: 2
                      }}
                    >
                      <Typography variant="body1" component="div">
                        {category.name}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center'
                        }}
                      >
                        <Tooltip
                          title={t(
                            isEnabled ? 'categories.recommendable' : 'categories.non_recommendable'
                          )}
                          placement="top"
                        >
                          <Checkbox
                            checked={isEnabled}
                            onChange={() => handleToggleChange(category.id)}
                            disabled={isLoading}
                            color="primary"
                            sx={{
                              '&.Mui-checked': {
                                color: 'success.main'
                              },
                              '&.Mui-disabled': {
                                opacity: 0.6
                              }
                            }}
                          />
                        </Tooltip>
                      </Box>
                    </Box>
                    {index < categories.length - 1 && <Divider sx={{ opacity: 0.3 }} />}
                  </Box>
                );
              })}
            </Card>
          )
        )}
      </Container>
    </Page>
  );
}
